/**
 * segmentReordering.ts - Utilities for reordering segment nodes within a switch
 *
 * Segment nodes are children of switch nodes and appear horizontally.
 * This module provides functions to reorder segments while ensuring
 * the default segment always stays rightmost.
 *
 * Related:
 *   - utils/nodeReordering.ts - General node reordering
 *   - nodes/SegmentNode.tsx - Segment node component
 */

import type { Edge, Node } from '@xyflow/react';
import { BRANCH_COLORS, DEFAULT_BRANCH_COLOR } from '../edges/branchEdgeStyles';
import type { BaseNodeData } from '../types';
import { NODE_TYPES } from '../types';
import { EDGE_TYPES, type BranchEdgeData, type PhantomAddPathEdgeData } from '../types/edges';
import type { ParentCaseInfo, SegmentNodeData } from '../types/stepNodes';
import { isSwitchNodeData } from '../types/typeGuards';

interface SwitchCaseLike {
	id?: string;
	name?: string;
	isDefault?: boolean;
}

interface NormalizedSegmentCaseInfo {
	caseId: string;
	caseIndex: number;
	caseName: string;
	caseColor: string;
	isDefault: boolean;
}

function buildSwitchOutputHandles(cases: SwitchCaseLike[]): string[] {
	if (cases.length === 0) {
		return ['default'];
	}

	return cases.map((candidate, index) => (candidate.isDefault ? 'default' : `case-${index}`));
}

function getSegmentCaseName(
	segment: SegmentNodeData,
	caseStatement: SwitchCaseLike | undefined,
	caseIndex: number
): string {
	const explicitName = (segment.segmentName || caseStatement?.name || '').trim();
	if (explicitName.length > 0) {
		return explicitName;
	}

	return segment.isDefault ? 'Default' : `Case ${caseIndex + 1}`;
}

/**
 * Get all segment nodes for a given switch node, ordered by segmentIndex
 *
 * Note: Uses the segment's parentSwitchNodeId data property to find segments,
 * not edge sources. This is because branch edges use T-junction visual style
 * where the source is the step BEFORE the switch, not the switch itself.
 */
export function getOrderedSegments(
	parentSwitchNodeId: string,
	nodes: Node<BaseNodeData>[],
	_edges: Edge[]
): Node<SegmentNodeData>[] {
	// Find segment nodes by their parentSwitchNodeId data property
	const segmentNodes = nodes.filter(
		n => n.type === NODE_TYPES.SEGMENT && (n.data as SegmentNodeData).parentSwitchNodeId === parentSwitchNodeId
	) as Node<SegmentNodeData>[];

	// Sort by segmentIndex
	return segmentNodes.sort((a, b) => (a.data.segmentIndex ?? 0) - (b.data.segmentIndex ?? 0));
}

export function applyLocalSegmentOrder(
	parentSwitchNodeId: string,
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	orderedSegmentIds: string[]
): {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
} {
	const segmentNodes = getOrderedSegments(parentSwitchNodeId, nodes, edges);
	const segmentById = new Map(segmentNodes.map(node => [node.id, node] as const));
	const newIndexBySegmentId = new Map<string, number>();
	const newIndexByCaseId = new Map<string, number>();
	const normalizedCaseInfoBySegmentId = new Map<string, NormalizedSegmentCaseInfo>();
	const normalizedCaseInfoByCaseId = new Map<string, NormalizedSegmentCaseInfo>();
	const normalizedCaseInfoByPreviousIndex = new Map<number, NormalizedSegmentCaseInfo>();

	const switchNode = nodes.find(node => node.id === parentSwitchNodeId && isSwitchNodeData(node.data));
	const existingCases =
		switchNode && isSwitchNodeData(switchNode.data) ? switchNode.data.automationStep.cases || [] : [];
	const existingCaseById = new Map<string, (typeof existingCases)[number]>();
	for (const caseStatement of existingCases) {
		if (caseStatement?.id) {
			existingCaseById.set(caseStatement.id, caseStatement);
		}
	}

	orderedSegmentIds.forEach((segmentId, index) => {
		const segmentNode = segmentById.get(segmentId);
		if (!segmentNode) {
			return;
		}

		const segmentData = segmentNode.data;
		const caseStatement = existingCaseById.get(segmentData.caseId);
		const caseInfo: NormalizedSegmentCaseInfo = {
			caseId: segmentData.caseId,
			caseIndex: index,
			caseName: getSegmentCaseName(segmentData, caseStatement, index),
			caseColor: segmentData.isDefault ? DEFAULT_BRANCH_COLOR : BRANCH_COLORS[index % BRANCH_COLORS.length],
			isDefault: segmentData.isDefault,
		};

		newIndexBySegmentId.set(segmentId, index);
		newIndexByCaseId.set(segmentData.caseId, index);
		normalizedCaseInfoBySegmentId.set(segmentId, caseInfo);
		normalizedCaseInfoByCaseId.set(segmentData.caseId, caseInfo);
		normalizedCaseInfoByPreviousIndex.set(segmentData.segmentIndex, caseInfo);
	});

	const nextCases = orderedSegmentIds
		.map(segmentId => segmentById.get(segmentId)?.data.caseId)
		.filter((caseId): caseId is string => !!caseId)
		.map(caseId => existingCaseById.get(caseId))
		.filter((caseStatement): caseStatement is NonNullable<(typeof existingCases)[number]> => !!caseStatement);

	const updatedNodes = nodes.map(node => {
		if (node.type === NODE_TYPES.SEGMENT) {
			const segData = node.data as SegmentNodeData;
			if (segData.parentSwitchNodeId !== parentSwitchNodeId) {
				return node;
			}

			const newIndex = newIndexBySegmentId.get(node.id);
			const segmentCaseInfo = normalizedCaseInfoBySegmentId.get(node.id);
			if (newIndex === undefined || !segmentCaseInfo) {
				return node;
			}

			return {
				...node,
				data: {
					...segData,
					label: segData.isDefault ? segData.label : `Segment #${newIndex + 1}`,
					segmentIndex: newIndex,
					caseColor: segmentCaseInfo.caseColor,
				},
			};
		}

		if (node.id === parentSwitchNodeId && isSwitchNodeData(node.data) && nextCases.length > 0) {
			return {
				...node,
				data: {
					...node.data,
					automationStep: {
						...node.data.automationStep,
						cases: nextCases,
					},
					summary: `${nextCases.length} branches`,
					outputHandles: buildSwitchOutputHandles(nextCases),
					isValid: nextCases.length > 0,
					validationErrors: nextCases.length > 0 ? [] : ['At least one branch is required'],
					stepData: {
						...(node.data.stepData || {}),
						hasContent: nextCases.length > 0,
					},
				},
			};
		}

		const parentCaseInfo = (node.data as { parentCaseInfo?: ParentCaseInfo } | undefined)?.parentCaseInfo;
		if (!parentCaseInfo || parentCaseInfo.isBehaviorCase || parentCaseInfo.parentSwitchNodeId !== parentSwitchNodeId) {
			return node;
		}

		const resolvedParentCaseInfo = parentCaseInfo.caseId
			? normalizedCaseInfoByCaseId.get(parentCaseInfo.caseId)
			: typeof parentCaseInfo.caseIndex === 'number'
				? normalizedCaseInfoByPreviousIndex.get(parentCaseInfo.caseIndex)
				: undefined;
		if (!resolvedParentCaseInfo) {
			return node;
		}

		return {
			...node,
			data: {
				...node.data,
				parentCaseInfo: {
					...parentCaseInfo,
					caseIndex: resolvedParentCaseInfo.caseIndex,
					caseName: resolvedParentCaseInfo.caseName,
					isDefault: resolvedParentCaseInfo.isDefault,
					caseColor: resolvedParentCaseInfo.caseColor,
					parentSwitchNodeId,
					caseId: resolvedParentCaseInfo.caseId,
				},
			},
		};
	});

	const updatedEdges = edges.map(edge => {
		if (edge.type === EDGE_TYPES.BRANCH) {
			const edgeData = edge.data as BranchEdgeData | undefined;
			const belongsToSwitch = edgeData?.switchNodeId === parentSwitchNodeId || edge.source === parentSwitchNodeId;
			if (!belongsToSwitch) {
				return edge;
			}

			const resolvedEdgeCaseInfo =
				normalizedCaseInfoBySegmentId.get(edge.target) ||
				(edgeData?.caseId
					? normalizedCaseInfoByCaseId.get(edgeData.caseId)
					: typeof edgeData?.caseIndex === 'number'
						? normalizedCaseInfoByPreviousIndex.get(edgeData.caseIndex)
						: undefined);
			if (!resolvedEdgeCaseInfo) {
				return edge;
			}

			return {
				...edge,
				data: {
					...edgeData,
					caseId: resolvedEdgeCaseInfo.caseId,
					caseIndex: resolvedEdgeCaseInfo.caseIndex,
					totalCases: orderedSegmentIds.length,
					isDefault: resolvedEdgeCaseInfo.isDefault,
					caseName: resolvedEdgeCaseInfo.caseName,
					caseColor: resolvedEdgeCaseInfo.caseColor,
					switchNodeId: parentSwitchNodeId,
				},
			};
		}

		if (edge.type === EDGE_TYPES.PHANTOM_ADD_PATH) {
			const edgeData = edge.data as PhantomAddPathEdgeData | undefined;
			const phantomTargetId = `phantom-target-${parentSwitchNodeId}`;
			const belongsToSwitch = edgeData?.switchNodeId === parentSwitchNodeId || edge.target === phantomTargetId;
			if (!belongsToSwitch) {
				return edge;
			}

			return {
				...edge,
				data: {
					...edgeData,
					switchNodeId: parentSwitchNodeId,
					segmentCount: orderedSegmentIds.length,
				},
			};
		}

		return edge;
	});

	return {
		nodes: updatedNodes,
		edges: updatedEdges,
	};
}
